import React, { useState } from 'react';
import axios from 'axios';

// ── Nakładka konfiguracji skrzynki e-mail (OPENkontakt) ───────────────────────
// Okno na tle strony (ten sam wzorzec co MMOnboardingWizard) pokazywane w module
// e-mail, gdy firma NIE ma jeszcze skonfigurowanej skrzynki IMAP.
// Po udanym podłączeniu pokazuje EKRAN POTWIERDZENIA (skrzynka założona +
// synchronizacja + info o regułach), a nie od razu zamyka — inaczej klient
// trafiał do pustej listy i było to mylące.
//
// Props:
//   onDone  - callback po udanym podłączeniu/synchronizacji (odswieza liste w module e-mail)
//   onClose - zamkniecie nakładki (× lub "Przejdź do skrzynki")

const COLOR = '#15803d';

// Otwiera asystenta AI z wiedzą o danym kontekscie (flaga nasłuchiwana w AIAssistant.tsx).
function openAssistant(context: string) {
  try {
    localStorage.setItem('assistant_context', context);
    localStorage.setItem('assistant_open_request', String(Date.now()));
  } catch (e) { /* ignoruj */ }
}

// Wspólny link "zapytaj asystenta" — jednolity wygląd na wszystkich oknach.
function AssistantLink({ context }: { context: string }) {
  return (
    <div style={{ textAlign: 'center', marginTop: 14 }}>
      <button type="button" onClick={() => openAssistant(context)}
        style={{ border: 'none', background: 'transparent', color: COLOR, fontSize: 13, cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}>
        🤖 Masz pytania? Zapytaj asystenta AI
      </button>
    </div>
  );
}

export default function MMEmailSetupBanner({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const [form, setForm] = useState({ name: '', login: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [manual, setManual] = useState({ imap_host: '', imap_port: '993', smtp_host: '', smtp_port: '587' });

  // Po udanym podłączeniu: ekran potwierdzenia + synchronizacja.
  const [done, setDone] = useState(false);
  const [savedName, setSavedName] = useState('');
  const [savedLogin, setSavedLogin] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);

  const showManual = attempts >= 2;

  const saveAccount = async (host: string, port: string, smtpHost: string, smtpPort: string) => {
    const created = await axios.post('/api/settings/imap-accounts', {
      name: form.name, host, port, login: form.login, password: form.password,
    });
    const newId = created.data?.id;
    if (newId && (smtpHost || smtpPort)) {
      try {
        await axios.patch('/api/settings/imap-accounts/' + newId, {
          name: form.name, host, port, login: form.login, password: form.password,
          smtp_host: smtpHost || '', smtp_port: smtpPort || '587',
        });
      } catch (e) { /* SMTP opcjonalny — nie blokuj sukcesu */ }
    }
  };

  // Po zapisie skrzynki — przejdz na ekran potwierdzenia (nie zamykaj nakładki).
  const goToConfirm = () => {
    setSavedName(form.name);
    setSavedLogin(form.login);
    setDone(true);
    onDone(); // odswiez liste kont w module (zeby nakładka nie wrocila po zamknieciu)
  };

  const connectAuto = async () => {
    setError('');
    if (!form.name.trim() || !form.login.trim() || !form.password) {
      setError('Podaj nazwę, adres e-mail i hasło.');
      return;
    }
    setBusy(true);
    try {
      const det = await axios.post('/api/email/detect-config', {
        host: 'auto', port: '993', login: form.login, password: form.password,
      });
      const d = det.data || {};
      if (d.status === 'ok' && d.detected_host) {
        await saveAccount(d.detected_host, d.detected_port || '993',
          d.detected_smtp_host || '', d.detected_smtp_port || '587');
        goToConfirm();
      } else {
        const a = attempts + 1;
        setAttempts(a);
        setError(a < 2
          ? 'Nie udało się połączyć. Najczęstsza przyczyna to literówka w adresie e-mail albo błędne hasło. Sprawdź jedno i drugie — to hasło do Twojej skrzynki e-mail (u niektórych dostawców trzeba wygenerować „hasło aplikacji"), a nie hasło do OPENkontakt.'
          : 'Nadal nie działa. Jeśli adres e-mail i hasło są na pewno poprawne, wpisz dane serwera ręcznie poniżej.');
      }
    } catch (e: any) {
      const a = attempts + 1;
      setAttempts(a);
      setError(a < 2
        ? 'Nie udało się połączyć. Najczęstsza przyczyna to literówka w adresie e-mail albo błędne hasło. Sprawdź jedno i drugie — to hasło do Twojej skrzynki e-mail (u niektórych dostawców trzeba wygenerować „hasło aplikacji"), a nie hasło do OPENkontakt.'
        : 'Nadal nie działa. Jeśli adres e-mail i hasło są na pewno poprawne, wpisz dane serwera ręcznie poniżej.');
    }
    setBusy(false);
  };

  const connectManual = async () => {
    setError('');
    if (!form.name.trim() || !form.login.trim() || !form.password) {
      setError('Podaj nazwę, adres e-mail i hasło.');
      return;
    }
    if (!manual.imap_host.trim()) {
      setError('Podaj adres serwera IMAP.');
      return;
    }
    setBusy(true);
    try {
      const test = await axios.post('/api/email/test-connection', {
        host: manual.imap_host, port: manual.imap_port || '993',
        login: form.login, password: form.password,
      });
      if (test.data?.status === 'ok') {
        await saveAccount(manual.imap_host, manual.imap_port || '993',
          manual.smtp_host || '', manual.smtp_port || '587');
        goToConfirm();
      } else {
        setError('Nie udało się połączyć z podanym serwerem. Sprawdź adres serwera IMAP, port oraz hasło.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Nie udało się połączyć z podanym serwerem. Sprawdź adres serwera IMAP, port oraz hasło.');
    }
    setBusy(false);
  };

  // Synchronizacja — pobiera wiadomości (POST /email/sync). Backend pobiera wszystko
  // synchronicznie i zwraca na końcu, wiec pokazujemy jasny komunikat "to chwilę potrwa".
  const runSync = async () => {
    setSyncing(true);
    try {
      await axios.post('/api/email/sync');
      setSynced(true);
      onDone(); // odswiez liste wiadomosci w module
    } catch (e) { /* nie blokuj — klient moze zsynchronizowac pozniej w module */ }
    setSyncing(false);
  };

  // ── style (1:1 z MMOnboardingWizard) ──────────────────────────────────────────
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 10001,
    background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: isMobile ? 12 : 24,
  };
  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column', maxHeight: '92vh',
  };
  const headerStyle: React.CSSProperties = {
    padding: isMobile ? '14px 18px' : '18px 24px', borderBottom: '1px solid #eef2f7',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
  };
  const bodyStyle: React.CSSProperties = {
    padding: isMobile ? '20px 18px' : '28px 32px', overflowY: 'auto', flex: 1,
  };
  const primaryBtn: React.CSSProperties = {
    padding: '11px 22px', borderRadius: 10, border: 'none', background: COLOR,
    color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  };
  const ghostBtn: React.CSSProperties = {
    padding: '11px 22px', borderRadius: 10, border: '1px solid #d1d5db', background: '#fff',
    color: '#374151', fontSize: 15, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #d1d5db',
    fontSize: 14, marginBottom: 10, boxSizing: 'border-box',
  };
  const h1: React.CSSProperties = { fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#111827', margin: '0 0 8px' };
  const sub: React.CSSProperties = { fontSize: 14, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.5 };

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={headerStyle}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>
            {done ? 'Skrzynka podłączona' : 'Konfiguracja skrzynki e-mail'}
          </span>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: '#9ca3af', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
        </div>
        <div style={bodyStyle}>
          {!done ? (
            // ─── EKRAN 1: formularz podłączenia ───
            <>
              <div style={{ fontSize: 40, marginBottom: 10, textAlign: 'center' }}>✉️</div>
              <h1 style={{ ...h1, textAlign: 'center' }}>Podłącz skrzynkę e-mail</h1>
              <p style={{ ...sub, textAlign: 'center' }}>
                Nie masz jeszcze podłączonej skrzynki. Podłącz ją, aby OPENkontakt obsługiwał
                także wiadomości e-mail od klientów. Podaj dane skrzynki — resztę (serwery) wykryjemy automatycznie.
              </p>

              <input style={inputStyle} placeholder="Nazwa (np. Poczta firmowa)" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} />
              <input style={inputStyle} placeholder="Adres e-mail" value={form.login} autoComplete="off"
                onChange={e => setForm({ ...form, login: e.target.value })} />
              <input style={inputStyle} placeholder="Hasło do skrzynki e-mail" type="password" value={form.password} autoComplete="new-password"
                onChange={e => setForm({ ...form, password: e.target.value })} />
              {error && <div style={{ color: showManual ? '#b45309' : '#dc2626', fontSize: 13, marginBottom: 10, lineHeight: 1.4 }}>{error}</div>}

              {showManual && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Ustawienia zaawansowane (serwery ręcznie)</div>
                  <input style={inputStyle} placeholder="Serwer IMAP (np. imap.twojadomena.pl)" value={manual.imap_host}
                    onChange={e => setManual({ ...manual, imap_host: e.target.value })} />
                  <input style={inputStyle} placeholder="Port IMAP (zwykle 993)" value={manual.imap_port}
                    onChange={e => setManual({ ...manual, imap_port: e.target.value })} />
                  <input style={inputStyle} placeholder="Serwer SMTP (np. smtp.twojadomena.pl)" value={manual.smtp_host}
                    onChange={e => setManual({ ...manual, smtp_host: e.target.value })} />
                  <input style={inputStyle} placeholder="Port SMTP (zwykle 587)" value={manual.smtp_port}
                    onChange={e => setManual({ ...manual, smtp_port: e.target.value })} />
                </div>
              )}

              <button onClick={showManual ? connectManual : connectAuto} disabled={busy}
                style={{ ...primaryBtn, width: '100%', opacity: busy ? 0.7 : 1 }}>
                {busy ? 'Łączę…' : (showManual ? 'Sprawdź i zapisz ręcznie' : 'Sprawdź i podłącz')}
              </button>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>
                🔒 Hasło jest szyfrowane i używane wyłącznie do połączenia z Twoją skrzynką.
              </p>
              <AssistantLink context="settings:imap" />
            </>
          ) : (
            // ─── EKRAN 2: potwierdzenie + synchronizacja + reguły ───
            <>
              <div style={{ fontSize: 44, marginBottom: 10, textAlign: 'center' }}>✅</div>
              <h1 style={{ ...h1, textAlign: 'center' }}>Skrzynka e-mail podłączona!</h1>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 16px', marginBottom: 18, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#166534' }}>{savedName || 'Poczta firmowa'}</div>
                {savedLogin && <div style={{ fontSize: 13, color: '#15803d', marginTop: 2 }}>{savedLogin}</div>}
              </div>

              {!synced ? (
                <>
                  <p style={{ ...sub, marginBottom: 14 }}>
                    Aby załadować wiadomości do skrzynki, wykonaj pierwszą synchronizację.
                    Pobierzemy wiadomości z Twojego serwera — <strong>to może chwilę potrwać</strong>,
                    a wszystkie pojawią się naraz po zakończeniu.
                  </p>
                  <button onClick={runSync} disabled={syncing}
                    style={{ ...primaryBtn, width: '100%', opacity: syncing ? 0.7 : 1 }}>
                    {syncing ? 'Synchronizuję… pobieram wiadomości…' : 'Synchronizuj i załaduj wiadomości'}
                  </button>
                </>
              ) : (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '12px 16px', marginBottom: 14, textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#166534' }}>✓ Wiadomości załadowane</div>
                  <div style={{ fontSize: 13, color: '#15803d', marginTop: 2 }}>Twoja skrzynka jest gotowa.</div>
                </div>
              )}

              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 16px', marginTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>💡 Porządkuj wiadomości automatycznie</div>
                <div style={{ fontSize: 13, color: '#1e3a8a', lineHeight: 1.5 }}>
                  Powtarzające się wiadomości możesz obsługiwać regułami — na podstawie nadawcy lub tematu
                  system automatycznie przydzieli e-mail do właściwej osoby albo go zamknie.
                  Reguły ustawisz w Ustawieniach → Konta Email.
                </div>
              </div>

              <button onClick={onClose}
                style={{ ...(synced ? primaryBtn : ghostBtn), width: '100%', marginTop: 18 }}>
                Przejdź do skrzynki →
              </button>
              <AssistantLink context="settings:imap:rules" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
